import { useEffect } from 'react'
import { Flex, Spinner, Text } from '@chakra-ui/react'

import { setUnauthorizedHandler } from '@/lib/apiClient'
import { getAccessToken, isAccessTokenValid, setSession } from '@/services/jwt.service'
import { useAuthStore } from '@/store/authStore'

const AuthBootstrapping = () => (
  <Flex
    minHeight="100dvh"
    align="center"
    justify="center"
    gap="2"
    bg="bg.canvas"
    role="status"
    aria-live="polite"
  >
    <Spinner size="sm" color="fg.subtle" borderWidth="2px" />
    <Text textStyle="bodySecondary">Restoring your session…</Text>
  </Flex>
)

/**
 * Wraps the entire application. On mount it restores a session from the stored
 * access token before any route renders, so a signed-in visitor is never briefly
 * bounced to the login screen on a hard refresh.
 *
 * Children stay unmounted until the check settles.
 */
const Auth = ({ children }) => {
  const tokenLoading = useAuthStore((state) => state.tokenLoading)
  const loginWithToken = useAuthStore((state) => state.loginWithToken)
  const logoutService = useAuthStore((state) => state.logoutService)

  // A 401 from any request ends the session; the route guards handle the rest.
  useEffect(() => {
    setUnauthorizedHandler(() => logoutService())
    return () => setUnauthorizedHandler(null)
  }, [logoutService])

  useEffect(() => {
    const accessToken = getAccessToken()

    // No token, or one that already expired: nothing to restore.
    if (!isAccessTokenValid(accessToken)) {
      logoutService()
      return
    }

    setSession(accessToken)
    loginWithToken()
  }, [loginWithToken, logoutService])

  if (tokenLoading) return <AuthBootstrapping />

  return children
}

export default Auth
